<template>
  <div class="row">
    <div class="col-md-12 mb-2">
      <label>{{ $t("bulletin.listeClasses") }}</label>
      <a-select
        show-search
        :value="selectedClasse || undefined"
        :placeholder="$t('emploi.listeClasses')"
        option-filter-prop="children"
        :filter-option="filterOption"
        :loading="classesLoading"
        :disabled="classesLoading"
        class="select-class ml-3"
        @change="handleClassChange"
      >
        <a-select-option
          v-for="classe in classes"
          :key="classe._id"
          :value="classe._id"
        >
          {{ classe.name }}
        </a-select-option>
      </a-select>

      <label class="label-spacing">{{
        $t("bulletin.matieres")
      }}</label>
      <a-select
        show-search
        option-filter-prop="children"
        :filter-option="filterOption"
        :placeholder="$t('bulletin.matieres')"
        class="select-subject ml-3"
        :loading="subjectLoading"
        :disabled="subjectLoading || !selectedClasse"
        :value="selectedSubject || undefined"
        @change="handleSubjectChange"
      >
        <a-select-option
          v-for="subject in subjectList"
          :key="subject._id"
          :value="subject._id"
        >
          {{ subject.name }}
        </a-select-option>
      </a-select>

      <label class="label-spacing">{{
        $t("emploi.trimestre")
      }}</label>
      <a-select
        :placeholder="$t('paiement.selectionnerMois')"
        class="select-trimester ml-3"
        :value="selectedTrimester || '1'"
        @change="handleTrimesterChange"
      >
        <a-select-option
          v-for="trimester in ['1', '2', '3']"
          :key="trimester"
          :value="trimester"
        >
          {{ trimester }}
        </a-select-option>
      </a-select>
    </div>
    <div class="col-xl-12">
      <div
        class="card mb-2"
        v-if="selectedClasse && selectedSubject && currentLevel"
      >
        <a-table
          :columns="columns"
          :data-source="studentsList"
          :pagination="false"
          :loading="tableLoading"
          :scroll="{ x: 'max-content' }"
        >
          <template slot="studentName" slot-scope="text, record">
            <strong>{{ record.fullName }}</strong>
          </template>
          <template
            v-for="markTypeKey in markTypesKeys"
            :slot="markTypeKey"
            slot-scope="text, record"
          >
            <div
              v-if="
                selectedSubjectConfig &&
                selectedSubjectConfig.markTypes &&
                selectedSubjectConfig.markTypes[markTypeKey] &&
                selectedSubjectConfig.markTypes[markTypeKey].enabled
              "
              :key="`${markTypeKey}-${record._id}`"
              class="mark-type-column"
            >
              <div class="mark-type-header-cell">
                <span
                  v-if="
                    selectedSubjectConfig.markTypes[markTypeKey].count > 1
                  "
                >
                  (×{{ selectedSubjectConfig.markTypes[markTypeKey].count }})
                </span>
                <span
                  v-if="
                    selectedSubjectConfig.markTypes[markTypeKey].subsubject
                  "
                  class="subsubject-label"
                >
                  {{
                    getSubsubjectName(
                      selectedSubjectConfig.markTypes[markTypeKey].subsubject
                    )
                  }}
                </span>
              </div>
              <div
                v-if="isExempted(record._id, markTypeKey)"
                class="exempted-notice-cell"
              >
                <a-alert
                  :message="$t('bulletin.alreadyExempt')"
                  type="warning"
                  banner
                  closable
                  @close="
                    () => exemptMarkType(record._id, markTypeKey, false)
                  "
                />
              </div>
              <div v-else class="mark-inputs-cell">
                <div
                  v-for="(markValue, index) in getMarksArray(
                    record._id,
                    markTypeKey,
                    selectedSubjectConfig.markTypes[markTypeKey].count
                  )"
                  :key="index"
                  class="mark-input-row"
                  :class="markValue < 0 || markValue > 20 ? 'danger' : ''"
                >
                  <span
                    v-if="
                      selectedSubjectConfig.markTypes[markTypeKey].count > 1
                    "
                    class="mark-index"
                  >
                    #{{ index + 1 }}
                  </span>
                  <a-input-number
                    :value="markValue"
                    class="mark-input-number"
                    :min="0"
                    :max="20"
                    @change="
                      (val) => {
                        updateMarkAtIndex(
                          val,
                          record._id,
                          markTypeKey,
                          index,
                          selectedSubjectConfig.markTypes[markTypeKey]
                        );
                      }
                    "
                  ></a-input-number>
                  <a-popconfirm
                    :title="$t('bulletin.confirmExemptStudent')"
                    @confirm="() => exemptMarkType(record._id, markTypeKey, true)"
                    v-if="!isExempted(record._id, markTypeKey)"
                  >
                    <a-icon
                      type="stop"
                      class="exempt-icon"
                      :title="$t('bulletin.exempt')"
                    />
                  </a-popconfirm>
                  <a-popconfirm
                    :title="$t('bulletin.confirmUnexemptStudent')"
                    @confirm="
                      () => exemptMarkType(record._id, markTypeKey, false)
                    "
                    v-else
                  >
                    <a-icon
                      type="undo"
                      class="exempt-icon"
                      :title="$t('bulletin.alreadyExempt')"
                    />
                  </a-popconfirm>
                </div>
              </div>
            </div>
          </template>
          <template slot="moyenne" slot-scope="text, record">
            <strong>{{ calculateMoyenne(record._id) }}</strong>
          </template>
        </a-table>
      </div>
      <div
        v-if="hasUnsavedChanges && selectedClasse && selectedSubject"
        class="row my-2"
      >
        <div class="col-xl-12">
          <a-alert
            :message="$t('all.unsavedChangesInfo')"
            type="warning"
            show-icon
            closable
            @close="() => {}"
          />
        </div>
      </div>
      <div
        class="row"
        v-if="selectedClasse && selectedSubject && studentsList.length !== 0"
      >
        <div class="col-xl-7">{{ $t("bulletin.toutEleves") }}</div>
        <div class="col-xl-3">
          <a-button
            :loading="saveLoading"
            class="save-all-button"
            type="primary"
            @click="saveAll()"
          >
            {{ $t("bulletin.enregistrerTout") }}<a-icon type="check" />
          </a-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from "@/services/axios";
import marksCommon from "../mixins/marksCommon";
import "../mixins/marksCommon.css";

export default {
  name: "BySubject",
  mixins: [marksCommon],
  computed: {
    currentLevel() {
      return this.getCurrentLevel(this.selectedClasse);
    },
    columns() {
      const cols = [
        {
          title: this.$t("menu.eleves"),
          dataIndex: "fullName",
          key: "studentName",
          fixed: "left",
          width: 200,
          scopedSlots: { customRender: "studentName" },
        },
      ];

      if (!this.selectedSubjectConfig) return cols;

      // Add columns for enabled markTypes, but put "synth" last
      const otherMarkTypes = this.markTypesKeys.filter(key => key !== 'synth');
      const synthMarkType = 'synth';
      
      // Add other markTypes first
      otherMarkTypes.forEach((markTypeKey) => {
        if (this.selectedSubjectConfig.markTypes?.[markTypeKey]?.enabled) {
          cols.push({
            title: this.$t(`college.bulletin.config.markType.${markTypeKey}`),
            key: markTypeKey,
            scopedSlots: { customRender: markTypeKey },
          });
        }
      });
      
      // Add "synth" last if enabled
      if (this.selectedSubjectConfig.markTypes?.[synthMarkType]?.enabled) {
        cols.push({
          title: this.$t(`college.bulletin.config.markType.${synthMarkType}`),
          key: synthMarkType,
          scopedSlots: { customRender: synthMarkType },
        });
      }

      // Add "Moyenne" column at the end
      cols.push({
        title: () => {
          const h = this.$createElement;
          return h('strong', this.$t("bulletin.moyenne"));
        },
        key: "moyenne",
        scopedSlots: { customRender: "moyenne" },
        fixed: "right",
        width: 100,
      });

      return cols;
    },
  },
  async created() {
    await this.loadClasses();
  },
  data() {
    return {
      selectedClasse: null,
      selectedSubject: null,
      selectedTrimester: "1",
      subjectList: [],
      studentsList: [],
      markData: [],
      originalMarkData: [],
      tableLoading: false,
      subjectLoading: false,
      saveLoading: false,
      selectedSubjectConfig: null,
      hasUnsavedChanges: false,
    };
  },
  mounted() {
    // Prevent leaving page if there are unsaved changes
    window.addEventListener("beforeunload", this.handleBeforeUnload);
    // Notify parent of initial state
    this.$emit("unsaved-changes", this.hasUnsavedChanges);
  },
  beforeDestroy() {
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
  },
  methods: {
    async handleClassChange(value) {
      const confirmed = await this.checkUnsavedChanges();
      if (!confirmed) return;

      this.selectedClasse = value;
      this.selectedSubject = null;
      this.studentsList = [];
      this.markData = [];
      this.selectedSubjectConfig = null;
      this.resetUnsavedChanges();
      if (value) {
        await this.loadConfigForClass();
        await this.loadStudentsForClass();
      }
    },
    async handleSubjectChange(value) {
      const confirmed = await this.checkUnsavedChanges();
      if (!confirmed) return;

      this.selectedSubject = value;
      if (value) {
        this.selectedSubjectConfig = this.subjectList.find(
          (s) => s._id === value
        );
        await this.updateMarks();
      } else {
        this.markData = [];
        this.originalMarkData = [];
        this.resetUnsavedChanges();
      }
    },
    async handleTrimesterChange(value) {
      const confirmed = await this.checkUnsavedChanges();
      if (!confirmed) return;

      this.selectedTrimester = value;
      this.resetUnsavedChanges();
      if (this.selectedClasse && this.currentLevel) {
        await this.loadConfigForClass();
        if (this.selectedSubject) {
          await this.updateMarks();
        }
      }
    },
    async loadStudentsForClass() {
      if (!this.selectedClasse) return;
      try {
        this.studentsList = await this.loadStudents(this.selectedClasse);
      } catch (e) {
        this.$message.error(this.$t("error.erreur"));
      }
    },
    async loadConfigForClass() {
      if (!this.selectedClasse || !this.currentLevel) return;
      this.subjectLoading = true;
      try {
        this.subjectList = await this.loadConfig(
          this.currentLevel,
          this.selectedTrimester
        );
      } finally {
        this.subjectLoading = false;
      }
    },
    async updateMarks() {
      if (
        !this.selectedClasse ||
        !this.selectedSubject ||
        !this.currentLevel
      )
        return;
      this.tableLoading = true;
      try {
        const res = await apiClient.post("/bulletin/college/mark/filter", {
          query: {
            classroom: this.selectedClasse,
            subject: this.selectedSubject,
            trimester: parseInt(this.selectedTrimester),
          },
        });

        this.markData = [];
        this.originalMarkData = res.data || [];

        // Create a map of student marks
        const marksByStudent = {};
        (res.data || []).forEach((markDoc) => {
          const studentId = markDoc.student._id || markDoc.student;
          marksByStudent[studentId] = markDoc;
        });

        // For each student, create or get their mark data
        this.studentsList.forEach((student) => {
          const markDoc = marksByStudent[student._id];
          const subject = this.selectedSubjectConfig;

          let normalizedMarks;
          if (markDoc) {
            normalizedMarks = this.normalizeAllMarks(markDoc, subject);
          } else {
            // Initialize with nulls based on count
            normalizedMarks = {
              oral: [],
              cont1: [],
              cont2: [],
              synth: [],
              written: [],
              tp: [],
            };
            Object.keys(normalizedMarks).forEach((markTypeKey) => {
              const markTypeConfig = subject?.markTypes?.[markTypeKey];
              const count = markTypeConfig?.count || 1;
              normalizedMarks[markTypeKey] = Array(count).fill(null);
            });
          }

          this.markData.push({
            _id: markDoc?._id || null,
            student: student._id,
            subject: this.selectedSubject,
            marks: normalizedMarks,
            exemptions: markDoc?.exemptions || {
              oral: false,
              cont1: false,
              cont2: false,
              synth: false,
              written: false,
              tp: false,
            },
            subsubject: markDoc?.subsubject
              ? markDoc.subsubject._id || markDoc.subsubject
              : null,
          });
        });
      } catch (e) {
        this.$message.error(this.$t("error.erreur"));
      } finally {
        this.tableLoading = false;
      }
    },
    getMarksArray(studentId, markTypeKey, count) {
      return this.getMarksArrayGeneric(studentId, markTypeKey, count);
    },
    isExempted(studentId, markTypeKey) {
      return this.isExemptedGeneric(studentId, markTypeKey);
    },
    calculateMoyenne(studentId) {
      return this.calculateMoyenneGeneric('bySubject', studentId);
    },
    updateMarkAtIndex(
      newMark,
      studentId,
      markTypeKey,
      index,
      markTypeConfig
    ) {
      this.updateMarkAtIndexGeneric(
        newMark,
        studentId,
        markTypeKey,
        index,
        markTypeConfig
      );
    },
    async exemptMarkType(studentId, markTypeKey, exempted) {
      await this.exemptMarkTypeGeneric(studentId, markTypeKey, exempted);
    },
    async saveAll() {
      await this.saveAllGeneric();
    },
  },
};
</script>


